import { Injectable } from '@angular/core';
import { DbTransaction } from '../models/db/db-transaction';
import { RepositoryService } from '../baseServices/repository.service';
import { StoreName } from '../entities/StoreName';
import { OdometerReading } from '../entities/OdometerReading';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Subject } from 'rxjs';
import { Callback, CallbackAddMode, CallbackNames } from '../models/db/db-transaction-callback';
import { RecordNotFoundException, RecordSoftDeletedException, StateValidationException, ValidationException } from '../utils/exceptions';
import { GlobalConstants } from '../constant/constant';
import { Check, EntityHelper } from '../utils/entity-helper';
import { ArrayHelper } from '../utils/array-helper';
import { CreateOpenOdometerReadingDataDto } from '../dtos/odometer-reading-service-dtos';
import { BaseOdometerReadingDataDto } from '../dtos/odometer-reading-service-dtos/data-dtos/base-odometer-reading-data-dto';
import { CloseOdometerReadingDataDto, CreateClosedOdometerReadingDataDto } from '../dtos/odometer-reading-service-dtos/data-dtos/close-odometer-reading-data-dto';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { DbKeyType } from '../models/db/db-store';
import { LogService } from '../baseServices/logging/log.service';

@Injectable({
  providedIn: 'root',
})
export class OdometerReadingService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<OdometerReading>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor(private repository: RepositoryService, private logService: LogService) {}

  // #region Utility

  emitChange(odometerReading: OdometerReading, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: odometerReading, who, what });
  }

  private getEmitChangeCallback(odometerReading: OdometerReading, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForOdometerReading + '_' + odometerReading.RecordId,
      fn: this.emitChange.bind(this, odometerReading, who, what),
    };
  }

  private getValidateOdometerReadingCallback(odometerReading: OdometerReading): Callback {
    return {
      name: CallbackNames.ValidateOdometerReading,
      fn: this.validateOdometerReading.bind(this, odometerReading),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.OdometerReading], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getOdometerReadingByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<OdometerReading> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<OdometerReading>(StoreName.OdometerReading, recordId, OdometerReading, transaction);
    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as OdometerReading;

    if (this.checkIsOdometerReadingSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  async getOdometerReadingByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<OdometerReading> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<OdometerReading>(StoreName.OdometerReading, onlineId, OdometerReading, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as OdometerReading;

    if (this.checkIsOdometerReadingSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getOdometerReadingsByActivityLogId(activityLogId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<OdometerReading[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<OdometerReading>(StoreName.OdometerReading, GlobalConstants.KEY_NAMES.ActivityLogId, activityLogId, OdometerReading, transaction);
    let records = result as OdometerReading[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsOdometerReadingSoftDeleted(record));
    }

    return records;
  }

  async getOdometerReadingsByActivityLogRecordId(activityLogRecordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<OdometerReading[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<OdometerReading>(StoreName.OdometerReading, GlobalConstants.KEY_NAMES.ActivityLogRecordId, activityLogRecordId, OdometerReading, transaction);
    let records = result as OdometerReading[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsOdometerReadingSoftDeleted(record));
    }

    return records;
  }

  async getOdometerReadingsByUserIdDate(userId: number, date: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<OdometerReading[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const startDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(date));
    const endDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(date));

    const key: DbKeyType = {
      lower: [userId, startDate],
      upper: [userId, endDate],
    };

    const odometerReadings = await this.repository.getAllByIndex(StoreName.OdometerReading, GlobalConstants.KEY_NAMES.UserId_Date, key, OdometerReading, transaction);

    let records = odometerReadings;

    if (!includeDeleted) {
      records = odometerReadings.filter(odometerReading => !this.checkIsOdometerReadingSoftDeleted(odometerReading));
    }

    return records;
  }

  async getOdometerReadingsByUserId(userId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<OdometerReading[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const odometerReadings = await this.repository.getAllByIndex(StoreName.OdometerReading, GlobalConstants.KEY_NAMES.UserId, userId, OdometerReading, transaction);

    let records = odometerReadings;

    if (!includeDeleted) {
      records = odometerReadings.filter(odometerReading => !this.checkIsOdometerReadingSoftDeleted(odometerReading));
    }

    return records;
  }

  // #endregion
  // #region Low-level Create

  async createOdometerReading(who: DataChangeEmitWho, recordToCreate: OdometerReading, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LC1. Validate action
      await this.validateOdometerReadingCreation(transaction);

      // LC2. Idempotent check
      const existingRecord = (await this.getOdometerReadingsByUserIdDate(recordToCreate.UserId, recordToCreate.Date, transaction)).find(existingRecord =>
        this.checkCoreFieldsMatch(recordToCreate, existingRecord)
      );
      if (existingRecord != null) {
        return existingRecord.RecordId;
      }

      // LC3. Validate model
      await this.validateOdometerReading(recordToCreate, transaction);
    } else {
      transaction.addOnCommitCallback(this.getValidateOdometerReadingCallback(recordToCreate), CallbackAddMode.PushDuplicate);
    }

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.OdometerReading, transaction);

    // LC5. Get created record
    const createdRecord = await this.getOdometerReadingByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateOdometerReading(
    who: DataChangeEmitWho,
    recordToUpdate: OdometerReading,
    skipLocalCopyValidation: boolean,
    localCopy: OdometerReading | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LU1. Get existing record
      const existingRecord = await this.getOdometerReadingByRecordId(recordToUpdate.RecordId, transaction);

      // LU2. Validate action
      await this.validateOdometerReadingUpdate(recordToUpdate, transaction);

      // LU3. Idempotent check
      if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
        return;
      }

      // LU4. Validate local copy
      if (!skipLocalCopyValidation) {
        const checks: Check<OdometerReading>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy<OdometerReading>(localCopy, existingRecord, checks);
      }

      // LU5. Validate model
      await this.validateOdometerReading(recordToUpdate, transaction);
    } else {
      transaction.addOnCommitCallback(this.getValidateOdometerReadingCallback(recordToUpdate), CallbackAddMode.PushDuplicate);
    }

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.OdometerReading, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getOdometerReadingByRecordId(recordToUpdate.RecordId, transaction, true);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // #region Low-level Delete

  async softDeleteOdometerReading(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: OdometerReading | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LD1. Get existing record
    const recordToSoftDelete = await this.getOdometerReadingByRecordId(recordId, transaction, true);

    // LD2. Validate action
    await this.validateOdometerReadingSoftDeletion(recordToSoftDelete, transaction);

    // LD3. Idempotent check
    if (this.checkIsOdometerReadingSoftDeleted(recordToSoftDelete)) {
      return;
    }

    // LD4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<OdometerReading>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, recordToSoftDelete, checks);
    }

    // LD5. Prepare changes
    recordToSoftDelete.setSoftDeleted();

    // LD6. Validate model
    await this.validateSoftDeletedOdometerReading(recordToSoftDelete, transaction);

    // LD7. Repository call
    await this.repository.update(recordToSoftDelete, StoreName.OdometerReading, transaction);

    // LD8. Get soft deleted record
    const softDeletedRecord = await this.getOdometerReadingByRecordId(recordId, transaction, true);

    // LD9. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(softDeletedRecord, who, DataChangeEmitWhat.Delete));

    // LD10. Return
    return;
  }

  public async hardDeleteOdometerReading(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: OdometerReading | null, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // 1. Idempotent check
    try {
      // 2. Get existing record
      const existingRecord = await this.getOdometerReadingByRecordId(recordId, transaction, true);

      // 3. Local copy validate
      if (!skipLocalCopyValidation) {
        const checks: Check<OdometerReading>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
      }

      // 4. Validate hard delete
      await this.validateOdometerReadingHardDeletion(existingRecord, transaction);

      // 5. Repository call
      await this.repository.delete(recordId, StoreName.OdometerReading, transaction);

      // 6. Report changes
      transaction.addOnCommitCallback(this.getEmitChangeCallback(existingRecord, who, DataChangeEmitWhat.Delete));

      // 7. Return
      return;
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        return;
      }
      throw e;
    }
  }

  // #endregion
  // #region Low-level Validate Action

  async validateOdometerReadingCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateOdometerReadingUpdate(existingRecord: OdometerReading, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateOdometerReadingSoftDeletion(existingRecord: OdometerReading, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateOdometerReadingHardDeletion(existingRecord: OdometerReading, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
    // Add other validation checks as necessary
  }

  // #endregion
  // #region Low-level Validate Model

  async validateOdometerReading(odometerReading: OdometerReading, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const isOdometerReadingSoftDeleted = this.checkIsOdometerReadingSoftDeleted(odometerReading);
    if (isOdometerReadingSoftDeleted) {
      return;
    } else {
      if (odometerReading.OdometerStart < 0) {
        throw new ValidationException('OdometerStart must be non-negative');
      }

      if (odometerReading.OdometerEnd != null && odometerReading.OdometerEnd < 0) {
        throw new ValidationException('OdometerEnd must be non-negative');
      }

      if (odometerReading.OdometerEnd != null && odometerReading.OdometerEnd < odometerReading.OdometerStart) {
        throw new ValidationException('OdometerEnd must be greater than or equal to OdometerStart');
      }
    }

    const overlappingOdometerReadings = await this.getOverlappingOdometerReadings(odometerReading, transaction);

    if (overlappingOdometerReadings.length > 0) {
      throw new ValidationException('Odometer readings cannot overlap');
    }

    return;
  }

  async validateSoftDeletedOdometerReading(odometerReading: OdometerReading, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!this.checkIsOdometerReadingSoftDeleted(odometerReading)) {
      throw new StateValidationException('SoftDeleted', odometerReading);
    }

    return;
  }

  // #endregion
  // #region Low-level Sync

  // #endregion
  // #region Mid-level Get One

  async getOpenOdometerReading(userId: number, date: DateTimeType, existingTransaction?: DbTransaction): Promise<OdometerReading | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const odometerReadingsForUserAndDate = await this.getOdometerReadingsByUserIdDate(userId, date, transaction);
    const openOdometerReadings = odometerReadingsForUserAndDate.filter(odometerReading => this.checkIsOdometerReadingOpen(odometerReading));

    if (openOdometerReadings.length > 1) {
      this.logService.warn('OdometerReading.MultipleOpenReadings', { userId, date });
    }

    return ArrayHelper.firstOrDefault(openOdometerReadings);
  }

  // #endregion
  // #region Mid-level Get Many

  async getOverlappingOdometerReadings(targetOdometerReading: OdometerReading, existingTransaction?: DbTransaction): Promise<OdometerReading[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingOdometerReadings = await this.getOdometerReadingsByUserIdDate(targetOdometerReading.UserId, targetOdometerReading.Date, transaction);

    const overlappingOdometerReadings = existingOdometerReadings
      .reduce((acc, val) => acc.concat(val), [])
      .filter(
        existingOdometerReading =>
          !this.checkAnyOdometerReadingIdentifiersMatch(targetOdometerReading, existingOdometerReading) && this.checkOdometerReadingsOverlap(targetOdometerReading, existingOdometerReading)
      );

    return overlappingOdometerReadings;
  }

  // #endregion
  // #region Mid-level Create

  async createOpenOdometerReading(who: DataChangeEmitWho, data: CreateOpenOdometerReadingDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateOpenOdometerReadingCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildOpenOdometerReadingDto(data, data as BaseOdometerReadingDataDto, null, transaction);

    // MC3. Idempotent check
    const existingRecord = await this.getOpenOdometerReading(data.UserId, data.Date, transaction);
    if (existingRecord != null && this.checkCoreFieldsMatch(recordToCreate, existingRecord)) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const createdRecordId = await this.createOdometerReading(who, recordToCreate, transaction);

    // MC5. Return id
    return createdRecordId;
  }

  async createClosedOdometerReading(who: DataChangeEmitWho, data: CreateClosedOdometerReadingDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateClosedOdometerReadingCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildClosedOdometerReadingDto(data as CloseOdometerReadingDataDto, data as CreateOpenOdometerReadingDataDto, null, transaction);

    // MC3. Idempotent check
    const existingRecord = (await this.getOdometerReadingsByUserIdDate(data.UserId, data.Date, transaction)).find(existingRecord =>
      this.checkCoreFieldsMatch(recordToCreate, existingRecord)
    );
    if (existingRecord != null) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const createdRecordId = await this.createOdometerReading(who, recordToCreate, transaction);

    // MC5. Return id
    return createdRecordId;
  }

  // #endregion
  // #region Mid-level Update

  async closeOdometerReading(
    who: DataChangeEmitWho,
    recordId: number,
    data: CloseOdometerReadingDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: OdometerReading | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getOdometerReadingByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateOdometerReadingClosure(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildClosedOdometerReadingDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (this.checkIsOdometerReadingClosed(existingRecord)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<OdometerReading>[] = [{ name: 'OdometerReading', function: this.checkMileageFieldsMatch }];
      EntityHelper.validateLocalCopy<OdometerReading>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateOdometerReading(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  // #endregion
  // #region Mid-level Delete

  // #endregion
  // #region Mid-level Validate Action

  async validateOpenOdometerReadingCreation(data: CreateOpenOdometerReadingDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateClosedOdometerReadingCreation(data: CreateClosedOdometerReadingDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateOdometerReadingClosure(data: CloseOdometerReadingDataDto, existingRecord: OdometerReading, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  // #endregion
  // #region Mid-level Validate Model

  async validateOpenOdometerReading(odometerReading: OdometerReading, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateOdometerReading(odometerReading, transaction);
    }

    if (!this.checkIsOdometerReadingOpen(odometerReading)) {
      throw new StateValidationException('Open', odometerReading);
    }
  }

  async validateClosedOdometerReading(odometerReading: OdometerReading, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateOdometerReading(odometerReading, transaction);
    }

    if (!this.checkIsOdometerReadingClosed(odometerReading)) {
      throw new StateValidationException('Closed', odometerReading);
    }

    if (!this.checkIsEndMilageGreaterOrEqualStartMilage(odometerReading)) {
      throw new ValidationException('End mileage must be greater than or equal to start mileage.');
    }
  }

  // #endregion
  // #region Mid-level Upsert

  // #endregion
  // #region Mid-level Sync

  async updateOdometerReadingWithId(who: DataChangeEmitWho, recordId: number, onlineId: number, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    let recordToUpdate = await this.getOdometerReadingByRecordId(recordId, transaction, true);
    recordToUpdate.Id = onlineId;

    await this.updateOdometerReading(who, recordToUpdate, true, null, transaction);
  }

  async upsertOdometerReadingFromSync(who: DataChangeEmitWho, recordToUpsert: OdometerReading, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getOdometerReadingByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;

      await this.updateOdometerReading(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createOdometerReading(who, recordToUpsert, transaction, true);
      }
    }
    return;
  }

  // #endregion
  // #region Build

  private async buildBaseOdometerReadingDto(data: BaseOdometerReadingDataDto, existingTransaction?: DbTransaction): Promise<OdometerReading> {
    return new OdometerReading({
      ActivityLogRecordId: data.ActivityLogRecordId,
      OdometerStart: data.OdometerStart,
      UserId: data.UserId,
      Date: DateTimeHelper.formatDateTimeAsDate(data.Date),
      IsDeleted: false,
    });
  }

  async buildOpenOdometerReadingDto(
    data: CreateOpenOdometerReadingDataDto,
    baseData: BaseOdometerReadingDataDto | null,
    existingRecord: OdometerReading | null,
    existingTransaction?: DbTransaction
  ): Promise<OdometerReading> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildBaseOdometerReadingDto(baseData as BaseOdometerReadingDataDto, transaction));

    await this.validateOpenOdometerReading(record, true, transaction);

    return record;
  }

  async buildClosedOdometerReadingDto(
    data: CloseOdometerReadingDataDto,
    baseData: CreateOpenOdometerReadingDataDto | null,
    existingRecord: OdometerReading | null,
    existingTransaction?: DbTransaction
  ): Promise<OdometerReading> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildOpenOdometerReadingDto(baseData as CreateOpenOdometerReadingDataDto, baseData, null, transaction));
    record.setOdometerEnd(data.OdometerEnd);

    await this.validateClosedOdometerReading(record, true, transaction);

    return record;
  }

  async buildReopenedOdometerReadingDto(existingOdometerReading: OdometerReading, existingTransaction?: DbTransaction): Promise<OdometerReading> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const record = existingOdometerReading.clone();
    record.OdometerEnd = null;

    await this.validateOpenOdometerReading(record, true, transaction);

    return record;
  }

  // #endregion
  // #region Check

  checkIsOdometerReadingOpen(odometerReading: OdometerReading): boolean {
    return !odometerReading.IsClosed;
  }

  checkIsOdometerReadingClosed(odometerReading: OdometerReading): boolean {
    return odometerReading.IsClosed;
  }

  checkOdometerReadingsOverlap(a: OdometerReading, b: OdometerReading): boolean {
    if (a.OdometerEnd === null && b.OdometerEnd === null) {
      return true;
    }

    const aEnd = a.OdometerEnd ?? Number.MAX_SAFE_INTEGER;
    const bEnd = b.OdometerEnd ?? Number.MAX_SAFE_INTEGER;
    
    return a.OdometerStart < bEnd && b.OdometerStart < aEnd;
  }

  checkIsEndMilageGreaterOrEqualStartMilage(odometerReading: OdometerReading): boolean {
    return odometerReading.OdometerEnd >= odometerReading.OdometerStart;
  }

  checkAnyOdometerReadingIdentifiersMatch(a: OdometerReading, b: OdometerReading): boolean {
    if (!a?.RecordId && !a?.Id) {
      return false;
    }

    if (!b?.RecordId && !b?.Id) {
      return false;
    }

    return a?.RecordId === b?.RecordId || a?.Id === b?.Id;
  }

  private checkImmutableFieldsMatch(a: OdometerReading, b: OdometerReading): boolean {
    return a.UserId === b.UserId && a.Date === b.Date;
  }

  private checkActivityLogsMatch(a: OdometerReading, b: OdometerReading): boolean {
    return a.ActivityLogRecordId === b.ActivityLogRecordId || a.ActivityLogId === b.ActivityLogId;
  }

  private checkCoreFieldsMatch(a: OdometerReading, b: OdometerReading): boolean {
    return this.checkImmutableFieldsMatch(a, b) && this.checkMileageFieldsMatch(a, b) && this.checkActivityLogsMatch(a, b);
  }

  private checkMileageFieldsMatch(a: OdometerReading, b: OdometerReading): boolean {
    return a.OdometerStart === b.OdometerStart && a.OdometerEnd === b.OdometerEnd;
  }

  checkIsOdometerReadingSoftDeleted(odometerReading: OdometerReading): boolean {
    return odometerReading.IsDeleted;
  }

  // #endregion
  // #region Lookup

  // #endregion
  // #region Transform

  getSumTotalMileageForOdometerReadings(odometerReadings: OdometerReading[]): number {
    const closedOdometerReadings = odometerReadings.filter(odometerReading => this.checkIsOdometerReadingClosed(odometerReading));
    const totalMileage = closedOdometerReadings.reduce((total, odometerReading) => total + odometerReading.Mileage, 0);

    return totalMileage;
  }

  filterForUniqueOdometerReadings(odometerReadings: OdometerReading[]): OdometerReading[] {
    return odometerReadings.filter((e, i) => odometerReadings.findIndex(a => (a['Id'] && e['Id'] && a['Id'] === e['Id']) || a['RecordId'] === e['RecordId']) === i);
  }

  sortOdometerReadingsByEndOdometer(odometerReadings: OdometerReading[]): OdometerReading[] {
    return odometerReadings.sort((a, b) => a.OdometerStart - b.OdometerStart);
  }

  /**
   * Updates a specific property of an odometer reading
   * @param recordId - The record ID of the odometer reading to update
   * @param propertyName - The property name to update (e.g., 'StartPhotoBlobName', 'EndPhotoBlobName')
   * @param value - The new value for the property
   * @param existingTransaction - Optional database transaction
   */
  async updateOdometerReadingProperty(
    recordId: number,
    propertyName: keyof OdometerReading,
    value: any,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    
    try {
      const existingRecord = await this.getOdometerReadingByRecordId(recordId, transaction, true);
      if (!existingRecord) {
        throw new RecordNotFoundException(`OdometerReading with RecordId ${recordId} not found`);
      }

      // Update the specific property
      (existingRecord as any)[propertyName] = value;
      
      // Save the updated record
      await this.repository.update(existingRecord, StoreName.OdometerReading, transaction);
      
      // Log the update
      this.logService.event('OdometerReading.PropertyUpdated', {
        recordId: recordId,
        propertyName: propertyName,
        value: value
      });

    } catch (error) {
      transaction.rollback();
      throw error;
    }
  }

  // #endregion
  // #region Other

  // #endregion
}
